/**
 * Bottom sheet de Staff (design-system §7 · Bottom sheet): superficie con radio superior lg, asa de
 * 36×4 px, padding 16 px + safe area inferior, entrada 240 ms `ease-emphasized`, overlay
 * `--fm-overlay`. Se cierra tocando fuera o con Escape (el arrastre queda para una iteración
 * posterior). Sin Radix: `role="dialog"`, `aria-modal`, foco inicial dentro y ciclo de Tab.
 * Uso: explicar permisos antes de pedirlos, confirmar una acción, detalle de una marca.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { t } from '../../i18n';

import { cn } from './cn';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  /** Se llama al tocar fuera o pulsar Escape (solo si `dismissible`). */
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  /** Acciones: normalmente un único botón primario (§8.1). */
  footer?: ReactNode;
  dismissible?: boolean;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dismissible = true,
  className,
}: SheetProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const firstFocusable = panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? panel)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape' && dismissible) {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) {
      return;
    }
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end" onKeyDown={handleKeyDown}>
      <div
        aria-hidden="true"
        data-testid="sheet-overlay"
        className="absolute inset-0 bg-fm-overlay"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'fm-sheet-in fm-safe-bottom-gutter relative max-h-[90dvh] overflow-y-auto',
          'rounded-t-fm-lg bg-fm-surface px-fm-4 pt-fm-4 shadow-fm-2 outline-none',
          className,
        )}
      >
        <span
          aria-hidden="true"
          title={dismissible ? t('common.sheet.handle') : undefined}
          className="mx-auto mb-fm-4 block h-1 w-9 rounded-fm-pill bg-fm-border-strong"
        />
        <h2 id={titleId} className="font-display text-fm-xl font-bold text-fm-text">
          {title}
        </h2>
        {description ? (
          <p id={descriptionId} className="mt-fm-2 text-fm-base text-fm-text-secondary">
            {description}
          </p>
        ) : null}
        {children ? <div className="mt-fm-4 text-fm-base text-fm-text">{children}</div> : null}
        {footer ? <div className="mt-fm-6 flex flex-col gap-fm-3">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
